import { CustomPage, CustomPagesState, PublicSiteCustomRoutes } from './types.mod'
import { PublicSiteState } from '../../../public_site.main.mod'
import {
  route,
  mkWidget,
  AsyncState,
  HttpResult,
  IOWidget,
  Action,
  async,
  loadingAsyncState,
  unloadedAsyncState
} from 'widgets-for-react'
import { CustomRouteParams } from '../../cms/router/type.gen'
import React = require('react')
import { titleSplit } from '../utils/helpers'
import { t, getFixedT } from 'i18next'
import { Map, List, Set } from 'immutable'
import { FormStyle, FormElement, FormGroup, ErrorMessage, FieldSetLegend, headers } from '../utils/forms'
import { formClasses } from '../blocks/style.mod'
import { blockInviewHandler } from '../../drupal/rendering/inview_utils.mod'
import InView from 'react-intersection-observer'
import { getClassName } from '../../cms/blocks/utils.gen'
import { ExternalLink } from '../utils/external_link'
import { addLangToUrl, getLangFromUrl } from '../utils/history.mod'
import { Recaptcha } from '../utils/recaptcha'
import { loadProducts } from '../../../queries'
import { localeCountry, allCountries } from '../utils/countries'
import { countriesLocale } from '../utils/countries'
import { getDrupalLanguage } from '../application.mod'

export const requestDocuments: CustomPage<CustomPagesState['requestDocuments']> = {
  widget: (pair) =>
    mkWidget({
      run: (onDone) => (
        <main id="a11y-main" className="main main--request-bim">
          <div className="main__inner">
            <article className="page">
              <div className="page__inner">
                <InView
                  as="header"
                  onChange={(inView) => blockInviewHandler('pageHeader', inView, 'io-page-header', 'inview')}
                  triggerOnce={true}
                  threshold={0}
                  id="io-page-header"
                  className="pageheader--text cf"
                >
                  <div className="pageheader__inner bg--green-400">
                    <div className="pageheader__title container container--m gutter--3-5">
                      <h1 className="title--l">{titleSplit(t('BimAndDrawingsForm:Texts.PageTitle'))}</h1>
                    </div>
                  </div>
                </InView>
                <div className="page__content">
                  {bimAndDrawingsForm({ style: formClasses })(pair.snd.bimAndDrawingsFormState).run((action) =>
                    onDone((state) => ({ bimAndDrawingsFormState: action(state.bimAndDrawingsFormState) }))
                  )}
                </div>
              </div>
            </article>
          </div>
        </main>
      )
    }),
  route: route<{}, CustomRouteParams<PublicSiteState, PublicSiteCustomRoutes>>(
    `/:lang(${
    //allLanguages.join('|') why doesnt this work??
    'ae|au|be_nl|be_fr|cn|de|dk|es|fr|hk|ie|in|it|mx|my|nl|no|ru|se|uk|us|pl|na-fr|na-es'
    })?/request-documents`,
    () => ({
      kind: 'requestDocuments'
    })
  ),
  getState: (s: PublicSiteState) => s.requestDocuments,
  setState: (s: PublicSiteState, ps: CustomPagesState['requestDocuments']) => ({
    ...s,
    requestDocuments: ps
  }),
  updateStateBasesOnRoute: (s, r) =>
    r.kind != 'requestDocuments' ? s : { ...s, requestDocuments: { ...s.requestDocuments } }
}

export type bimAndDrawingsFormState = {
  data: {
    document: 'bim' | 'techdrawing' | 'both'
    products: Set<string>
    email: string
    country: string
    zipCode: string
    accept: boolean
  }
  availableProducts: AsyncState<List<string>>
  errors: Map<keyof bimAndDrawingsFormState['data'], string>
  submit: AsyncState<{}>
}

export const emptyBimAndDrawingsForm: () => bimAndDrawingsFormState = () => ({
  data: {
    document: undefined,
    products: Set(),
    email: undefined,
    country: localeCountry.get(getDrupalLanguage(getLangFromUrl())),
    zipCode: undefined,
    accept: false
  },
  availableProducts: unloadedAsyncState(),
  errors: Map(),
  submit: unloadedAsyncState()
})

const valid = (data: bimAndDrawingsFormState['data']) =>
  Map<keyof bimAndDrawingsFormState['data'], string>().withMutations((errors) => {
    if (!data.document) errors.set('document', t('BimAndDrawingsForm:Errors.NoDocument'))
    if (data.products.isEmpty()) errors.set('products', t('BimAndDrawingsForm:Errors.NoProduct'))
    if (!/.+@.+\..+/.test(data.email)) errors.set('email', t('Form:Errors.InvalidEmail'))
    if (data.country == 'global') errors.set('country', t('Form:Errors.NoCountry'))
    if (!data.accept) errors.set('accept', t('Form:Errors.NoTermsAccept'))
  })

const errorMessage = (errors: bimAndDrawingsFormState['errors'], dataField: keyof bimAndDrawingsFormState['data']) =>
  errors.has(dataField) && errors.get(dataField)

const send = async (data: bimAndDrawingsFormState['data']): Promise<HttpResult<{}>> => {
  const emailaddress = getFixedT(data.country)('ContactForm:Submit:SendTo')
  const formData = { ...data, emailTo: emailaddress }
  return fetch(`api/v1/mail/document`, {
    method: 'post',
    body: JSON.stringify(formData),
    headers: headers()
  }).then((_) => ({
    kind: 'result',
    status: 200,
    value: {}
  }))
}

export function bimAndDrawingsForm(options?: {
  style?: FormStyle
  key?: string
}): IOWidget<bimAndDrawingsFormState, Action<bimAndDrawingsFormState>> {
  return (state) =>
    mkWidget({
      run: (setState) => (
        <>
          {async<{}>()(state.submit).run((asyncaction) =>
            setState((form) => ({ ...form, submit: asyncaction(form.submit) }))
          )}

          {async<List<string>>()(state.availableProducts).run((asyncaction) =>
            setState((form) => ({ ...form, availableProducts: asyncaction(form.availableProducts) }))
          )}

          <InView
            as="section"
            onChange={(inView) =>
              blockInviewHandler('bimForm', inView, 'io-bim-form', getClassName(options, 'inview_classname', 'inview'))
            }
            triggerOnce={true}
            threshold={0.1}
            id="io-bim-form"
            className={getClassName(options, 'block_classname', '')}
          >
            <div className={getClassName(options, 'inner_classname', '')}>
              <header className={getClassName(options, 'header_classname', '')}>
                <h2 className={getClassName(options, 'title_classname', '')}>
                  {titleSplit(t('BimAndDrawingsForm:Texts.FormTitle'))}
                </h2>
              </header>

              {state.submit.kind == 'unloaded' ? (
                <div className={getClassName(options, 'content_classname', '')}>
                  <Recaptcha
                    callback={() => {
                      window.scrollTo(0, 0)
                      setState((s) => ({ ...s, submit: loadingAsyncState(() => send(s.data)) }))
                    }}
                  ></Recaptcha>

                  {(t('BimAndDrawingsForm:Texts.Intro.Text') ||
                    t('BimAndDrawingsForm:Texts.Intro.BoldText') ||
                    t('BimAndDrawingsForm:Texts.Intro.ListItem1') ||
                    t('BimAndDrawingsForm:Texts.Intro.ListItem2')) && (
                      <div className={getClassName(options, 'intro_classname', '')}>
                        {t('BimAndDrawingsForm:Texts.Intro.Text') && <p>{t('BimAndDrawingsForm:Texts.Intro.Text')}</p>}
                        {t('BimAndDrawingsForm:Texts.Intro.BoldText') &&
                          (t('BimAndDrawingsForm:Texts.Intro.ListItem1') ||
                            t('BimAndDrawingsForm:Texts.Intro.ListItem2')) && (
                            <>
                              <p>
                                <b>{t('BimAndDrawingsForm:Texts.Intro.BoldText')}</b>
                              </p>
                              <ul>
                                {t('BimAndDrawingsForm:Texts.Intro.ListItem1') && (
                                  <li>{t('BimAndDrawingsForm:Texts.Intro.ListItem1')}</li>
                                )}
                                {t('BimAndDrawingsForm:Texts.Intro.ListItem2') && (
                                  <li>{t('BimAndDrawingsForm:Texts.Intro.ListItem2')}</li>
                                )}
                              </ul>
                            </>
                          )}
                      </div>
                    )}

                  <form
                    className={getClassName(options, 'form_classname', '')}
                    noValidate={true}
                    onSubmit={(e) => {
                      e.preventDefault()
                      const errors = valid(state['data'])
                      if (errors.isEmpty()) {
                        grecaptcha.execute()
                      } else setState((s) => ({ ...s, errors: errors }))
                    }}
                  >
                    {/* Your request */}
                    {t('BimAndDrawingsForm:Texts.SubTitleYourRequest') && (
                      <h3 className={getClassName(options, 'subtitle_classname', '')}>
                        {t('BimAndDrawingsForm:Texts.SubTitleYourRequest')}
                      </h3>
                    )}

                    {/* Document Type */}
                    <FormGroup reversed={false}>
                      <FieldSetLegend
                        alignRow={true}
                        legend={t('BimAndDrawingsForm:Fields.DocumentSelector.Title')}
                        extraClasses={state.errors.has('document') ? 'form__group--error' : ''}
                      >
                        <FormElement
                          labelProps={{
                            htmlAttributes: { htmlFor: 'bim-form-document-type-bim-object' },
                            label: t('BimAndDrawingsForm:Fields.DocumentSelector.BIMobject')
                          }}
                          elementProps={{
                            kind: 'boolean',
                            htmlAttributes: {
                              type: 'radio',
                              name: 'bim-form-document-type',
                              id: 'bim-form-document-type-bim-object',
                              'aria-invalid': state.errors.has('document'),
                              'aria-describedby': 'a11y-bim-form-document-type-error',
                              required: true,
                              checked: state.data.document === 'bim',
                              onChange: (e) =>
                                setState((s) => ({
                                  ...s,
                                  data: { ...state.data, document: 'bim' },
                                  availableProducts: loadingAsyncState(() => loadProducts('bim', countriesLocale.get(state.data.country, 'global')))
                                }))
                            }
                          }}
                        />
                        <FormElement
                          labelProps={{
                            htmlAttributes: { htmlFor: 'bim-form-document-type-tech-drawing' },
                            label: t('BimAndDrawingsForm:Fields.DocumentSelector.TechDrawings')
                          }}
                          elementProps={{
                            kind: 'boolean',
                            htmlAttributes: {
                              type: 'radio',
                              name: 'bim-form-document-type',
                              id: 'bim-form-document-type-tech-drawing',
                              'aria-invalid': state.errors.has('document'),
                              'aria-describedby': 'a11y-bim-form-document-type-error',
                              required: true,
                              checked: state.data.document == 'techdrawing',
                              onChange: (e) =>
                                setState((s) => ({
                                  ...s,
                                  data: { ...state.data, document: 'techdrawing' },
                                  availableProducts: loadingAsyncState(() =>
                                    loadProducts('techdrawing', countriesLocale.get(state.data.country, 'global'))
                                  )
                                }))
                            }
                          }}
                        />
                        <FormElement
                          labelProps={{
                            htmlAttributes: { htmlFor: 'bim-form-document-type-both' },
                            label: t('BimAndDrawingsForm:Fields.DocumentSelector.Both')
                          }}
                          elementProps={{
                            kind: 'boolean',
                            htmlAttributes: {
                              type: 'radio',
                              name: 'bim-form-document-type',
                              id: 'bim-form-document-type-both',
                              'aria-invalid': state.errors.has('document'),
                              'aria-describedby': 'a11y-bim-form-document-type-error',
                              required: true,
                              checked: state.data.document == 'both',
                              onChange: (e) =>
                                setState((s) => ({
                                  ...s,
                                  data: { ...state.data, document: 'both' },
                                  availableProducts: loadingAsyncState(() => loadProducts('both', countriesLocale.get(state.data.country, 'global')))
                                }))
                            }
                          }}
                        />
                        {state.errors.has('document') && (
                          <ErrorMessage id="a11y-bim-form-document-type-error" message={state.errors.get('document')} />
                        )}
                      </FieldSetLegend>
                    </FormGroup>

                    {/* Available Products */}
                    {state.availableProducts.kind != 'unloaded' && (
                      <FormGroup reversed={true}>
                        {state.availableProducts.kind == 'loaded' ? (
                          state.availableProducts.value.isEmpty() ? (
                            <div className={getClassName(options, 'text_classname', '')}>
                              <p>{t('BimAndDrawingsForm:Fields.ProductSelector.NoProductsAvailable')}</p>
                            </div>
                          ) : (
                            <FieldSetLegend
                              extraClasses={state.errors.has('products') ? 'form__group--error' : ''}
                              legend={t('BimAndDrawingsForm:Fields.ProductSelector.Title')}
                              alignRow={false}
                            >
                              {state.availableProducts.kind == 'loaded' && (
                                <>
                                  {state.availableProducts.value.map<JSX.Element>((product) => (
                                    <FormElement
                                      labelProps={{
                                        htmlAttributes: { htmlFor: `bim-form-${product}-selection` },
                                        label: product
                                      }}
                                      elementProps={{
                                        kind: 'boolean',
                                        htmlAttributes: {
                                          type: 'checkbox',
                                          id: `bim-form-${product}-selection`,
                                          'aria-invalid': state.errors.has('products'),
                                          'aria-describedby': 'a11y-bim-form-document-type-error',
                                          required: true,
                                          checked: state.data.products.includes(product),
                                          onChange: (e) =>
                                            setState((s) => ({
                                              ...s,
                                              data: {
                                                ...s.data,
                                                products: e.currentTarget.checked
                                                  ? state.data.products.add(product)
                                                  : state.data.products.delete(product)
                                              }
                                            }))
                                        }
                                      }}
                                    />
                                  ))}
                                  {state.errors.has('products') && (
                                    <ErrorMessage
                                      id="a11y-bim-form-product-selection-error"
                                      message={state.errors.get('products')}
                                    />
                                  )}
                                </>
                              )}
                            </FieldSetLegend>
                          )
                        ) : (
                          //when failed/unauthorized/connection-error/loading
                          <div className={getClassName(options, 'text_classname', '')}>
                            <p>{t('BimAndDrawingsForm:Fields.ProductSelector.Loading')}</p>
                          </div>
                        )}
                      </FormGroup>
                    )}

                    {/* Your contact information */}
                    {t('BimAndDrawingsForm:Texts.SubTitleYourContactInfo') && (
                      <h3 className={getClassName(options, 'subtitle_classname', '')}>
                        {t('BimAndDrawingsForm:Texts.SubTitleYourContactInfo')}
                      </h3>
                    )}

                    {/* E-mail address */}
                    <FormElement
                      labelProps={{
                        htmlAttributes: { htmlFor: 'bim-form-email' },
                        label: t('Form:Fields.Email')
                      }}
                      elementProps={{
                        kind: 'input',
                        htmlAttributes: {
                          id: 'bim-form-email',
                          required: true,
                          inputMode: 'email',
                          autoComplete: 'email',
                          'aria-describedby': state.errors.has('email') ? 'a11y-bim-form-email-error' : '',
                          'aria-invalid': state.errors.has('email'),
                          value: state.data.email || '',
                          onChange: (e) =>
                            setState((s) => ({ ...s, data: { ...s.data, email: e.currentTarget.value } }))
                        }
                      }}
                      errorMessageProps={{
                        id: 'a11y-bim-form-email-error',
                        message: errorMessage(state.errors, 'email')
                      }}
                    />

                    {/* Country */}
                    <FormElement
                      labelProps={{
                        htmlAttributes: { htmlFor: 'bim-form-country' },
                        label: t('Form:Fields.Country.Title')
                      }}
                      elementProps={{
                        kind: 'select',
                        options: allCountries
                          .map((country) => ({
                            text:
                              country == 'global'
                                ? t('Form:Fields.Country.Placeholder')
                                : country,
                            htmlAttributes: {
                              disabled: country == 'global',
                              value: country
                            }
                          }))
                          .sort((a, b) => a.text.localeCompare(b.text)),
                        htmlAttributes: {
                          id: 'bim-form-country',
                          required: true,
                          'aria-required': true,
                          'aria-describedby': state.errors.has('country')
                            ? 'a11y-bim-form-country-error'
                            : 'a11y-bim-form-country-description',
                          'aria-invalid': state.errors.has('country'),
                          value: state.data.country,
                          onChange: (e) => {
                            const currentCountry = e.currentTarget.value
                            state.data.document
                              ? setState((s) => ({
                                ...s,
                                data: { ...state.data, country: currentCountry },
                                availableProducts: loadingAsyncState(() =>
                                  loadProducts(state.data.document, countriesLocale.get(currentCountry, 'global'))
                                )
                              }))
                              : setState((s) => ({
                                ...s,
                                data: { ...state.data, country: currentCountry }
                              }))
                          }
                        }
                      }}
                      errorMessageProps={{
                        id: 'a11y-bim-form-country-error',
                        message: errorMessage(state.errors, 'country')
                      }}
                      descriptionProps={{
                        id: 'a11y-bim-form-country-description',
                        message: t('Form:Fields.Country.Info')
                      }}
                    />

                    {/* ZIP code */}
                    <FormElement
                      labelProps={{
                        htmlAttributes: { htmlFor: 'bim-form-zip' },
                        label: t('Form:Fields.ZipCode')
                      }}
                      elementProps={{
                        kind: 'input',
                        htmlAttributes: {
                          id: 'bim-form-zip',
                          required: false,
                          autoComplete: 'postal-code',
                          'aria-describedby': state.errors.has('zipCode') ? 'a11y-bim-form-zip-error' : '',
                          'aria-invalid': state.errors.has('zipCode'),
                          value: state.data.zipCode || '',
                          onChange: (e) =>
                            setState((s) => ({ ...s, data: { ...s.data, zipCode: e.currentTarget.value } }))
                        }
                      }}
                      errorMessageProps={{
                        id: 'a11y-bim-form-zip-error',
                        message: errorMessage(state.errors, 'zipCode')
                      }}
                    />

                    {/* Accept terms */}
                    <FormElement
                      labelProps={{
                        htmlAttributes: {
                          htmlFor: 'bim-form-accept-terms'
                        },
                        label: t('Form:Fields.Terms.Text')
                      }}
                      elementProps={{
                        kind: 'boolean',
                        htmlAttributes: {
                          type: 'checkbox',
                          id: 'bim-form-accept-terms',
                          required: true,
                          'aria-describedby': state.errors.has('accept') ? 'a11y-bim-form-accept-terms-error' : '',
                          'aria-invalid': state.errors.has('accept'),
                          checked: state.data.accept,
                          onChange: (e) =>
                            setState((s) => ({ ...s, data: { ...s.data, accept: e.currentTarget.checked } }))
                        }
                      }}
                      errorMessageProps={{
                        id: 'a11y-bim-form-accept-terms-error',
                        message: errorMessage(state.errors, 'accept')
                      }}
                    />
                    <div className={getClassName(options, 'footer_classname', '')}>
                      <div className={getClassName(options, 'clickable_wrapper_classname', '')}>
                        <button type="submit" className={getClassName(options, 'clickable_primary_classname', '')}>
                          {t('BimAndDrawingsForm:Submit.Text')}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              ) : (
                // Thank you page
                <div className={getClassName(options, 'content_classname', '')}>
                  <p>{t('BimAndDrawingsForm:ThankYou.Text')}</p>
                  <div className={getClassName(options, 'footer_classname', '')}>
                    <div className={getClassName(options, 'clickable_wrapper_classname', '')}>
                      <button
                        className={getClassName(options, 'clickable_primary_classname', '')}
                        onClick={(_) => {
                          window.history.back()
                          setState((_) => emptyBimAndDrawingsForm())
                        }}
                      >
                        {t('BimAndDrawingsForm:ThankYou.ReturnPrevious')}
                      </button>
                    </div>
                    <div className={getClassName(options, 'clickable_wrapper_classname', '')}>
                      <ExternalLink
                        type="link"
                        className={getClassName(options, 'clickable_secondary_classname', '')}
                        to={addLangToUrl('/products')}
                        onClick={() => setState((_) => emptyBimAndDrawingsForm())}
                      >
                        {t('BimAndDrawingsForm:ThankYou.ReturnProducts')}
                      </ExternalLink>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </InView>
        </>
      )
    })
}
